





















































/* General */
import Vue from 'vue'
import { debounce } from 'lodash'

/* Mixins */

/* Components */

/* Helpers */

/* Data types */
import {
  IItemReference,
  ILinkedItemForListing,
  ISearchTaskResult,
} from '@/api/types/response.types'

/* API */
import SearchAPI from '@/api/modules/search.api'

export default Vue.extend({
  name: 'LinkExistingTasksPanel',

  props: {
    defaultProjectId: {
      type: Number,
      default: null,
    },
    originatingTask: {
      type: Object as () => IItemReference,
      default: null,
    },
    currentlyLinkedTasks: {
      type: Array as () => ILinkedItemForListing[],
      required: true,
    },
  },

  data() {
    return {
      isSearching: false,
      searchQuery: '',
      debounceGetResults: null,
      matchingTasks: [] as ISearchTaskResult[],
      searchedOnce: false,
      minSearchLength: 2,
    }
  },

  computed: {
    matchingTasksToDisplay(): ISearchTaskResult[] {
      const result = this.sortByProject(this.matchingTasks)

      if (this.originatingTask) {
        return result.filter(t => t.id !== this.originatingTask.id)
      }

      return result
    },
    searchEmpty(): boolean {
      return this.searchQuery.length < this.minSearchLength
    },
    noMatchingTasks(): boolean {
      return this.matchingTasksToDisplay.length === 0
    },
    showNoResults(): boolean {
      return (
        this.noMatchingTasks &&
        this.searchedOnce &&
        this.searchQuery.length >= this.minSearchLength
      )
    },
  },

  watch: {
    async searchQuery() {
      if (this.searchQuery.length >= 2) {
        await this.debounceGetResults()
        // @ts-ignore
        if (this.$refs.dropdownRef) {
          // @ts-ignore
          this.$refs.dropdownRef.$emit('visible', true)
        }
      } else {
        this.matchingTasks = [] as ISearchTaskResult[]
      }
    },
  },

  mounted() {
    // @ts-ignore
    this.$refs.searchRef.focus()
  },

  created() {
    this.debounceGetResults = debounce(this.getSearchTaskResults, 300)
  },

  methods: {
    // Show project name if the indexed item has a different project than its predecessor
    showProjectName(index: number): boolean {
      if (index === 0) {
        return true
      }

      if (
        this.matchingTasksToDisplay[index - 1].project.id !==
        this.matchingTasksToDisplay[index].project.id
      ) {
        return true
      }
    },

    sortByProject(tasks: ISearchTaskResult[]) {
      /* Show tasks in default project first */
      if (this.defaultProjectId) {
        const tasksInProject = [
          ...tasks.filter(t => t.project.id === this.defaultProjectId),
        ]
        const otherTasks = [
          ...tasks.filter(t => t.project.id !== this.defaultProjectId),
        ]

        tasksInProject.sort((a: ISearchTaskResult, b: ISearchTaskResult) => {
          if (a.isActive && !b.isActive) {
            return -1
          }
          if (!a.isActive && b.isActive) {
            return 1
          }
          return 0
        })

        otherTasks.sort((a: ISearchTaskResult, b: ISearchTaskResult) => {
          if (a.project.name.toLowerCase() < b.project.name.toLowerCase()) {
            return -1
          }
          if (a.project.name.toLowerCase() > b.project.name.toLowerCase()) {
            return 1
          }
          if (a.isActive && !b.isActive) {
            return -1
          }
          if (!a.isActive && b.isActive) {
            return 1
          }
          return 0
        })

        return [...tasksInProject, ...otherTasks]
      }

      tasks.sort((a: ISearchTaskResult, b: ISearchTaskResult) => {
        if (a.project.name.toLowerCase() < b.project.name.toLowerCase()) {
          return -1
        }
        if (a.project.name.toLowerCase() > b.project.name.toLowerCase()) {
          return 1
        }
        if (a.isActive && !b.isActive) {
          return -1
        }
        if (!a.isActive && b.isActive) {
          return 1
        }
        return 0
      })
      return tasks
    },

    // Only show description if there is a text match
    showDescription(task: ISearchTaskResult) {
      return task.description.includes('<span class="highlight">')
    },

    taskAlreadyLinked(id: number): boolean {
      return this.currentlyLinkedTasks.some(t => t.id === id)
    },

    async getSearchTaskResults() {
      if (this.searchQuery === '') {
        return
      }

      this.isSearching = true

      let result = await SearchAPI.post_Tasks(this.searchQuery)

      // Scrub away originating task, if matched...
      if (this.originatingTask) {
        result = result.filter(t => t.id !== this.originatingTask.id)
      }

      // Scrub away already linked tasks, if matched...
      result = result.filter(t => !this.taskAlreadyLinked(t.id))

      this.matchingTasks = result
      this.isSearching = false
      this.searchedOnce = true
    },

    linkSelectedTask(id: number) {
      this.$emit('selected-existing-task-id', id)
    },
  },
})
